import { useEffect, useState, type FormEvent } from 'react';
import Drawer from '../../components/Drawer';
import Spinner from '../../components/Spinner';
import {
  fosApi, fosError, formatDate, newRequestId, ROLE_HINT, ROLE_LABEL, scopeFromKey, type AssignablePerson, type AssignmentRole, type AssignmentView, type RelationshipView,
} from '../../lib/fosApi';
import { Avatar, ErrorNote, Icon, useDebounced } from './ui';

export type AssignMode =
  | { kind: 'assign'; role: AssignmentRole }
  | { kind: 'reassign'; assignment: AssignmentView }
  | { kind: 'end'; assignment: AssignmentView }
  | { kind: 'dates'; assignment: AssignmentView };

const today = () => new Date().toISOString().slice(0, 10);

function PersonSearch({ donorId, scopeKey, value, onPick }: { donorId: string; scopeKey: string; value: AssignablePerson | null; onPick: (p: AssignablePerson | null) => void }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const [results, setResults] = useState<AssignablePerson[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (dq.trim().length < 2) { setResults([]); return; }
    let cancelled = false;
    setBusy(true); setError('');
    void fosApi.searchPeople({ q: dq, donorId, scope: scopeFromKey(scopeKey) })
      .then((r) => { if (!cancelled) setResults(r.people); })
      .catch((c) => { if (!cancelled) setError(fosError(c).message); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [dq, donorId, scopeKey]);

  if (value) {
    return (
      <div className="fos-picked">
        <Avatar name={value.name} size={34} />
        <div><strong>{value.name}</strong><div className="subtitle">{value.ref ?? ''}{value.positions.length ? ` · ${value.positions.map((p) => p.name).join(', ')}` : ''}</div></div>
        <button type="button" className="btn small" onClick={() => onPick(null)}>Change</button>
      </div>
    );
  }
  return (
    <div className="fos-person-search">
      <div className="fos-search"><Icon name="search" size={16} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people in this scope by name" autoFocus /></div>
      {busy && <div className="fos-hint"><Spinner label="Searching" /></div>}
      <ErrorNote message={error} />
      {results.length > 0 && (
        <ul className="fos-results">
          {results.map((p) => (
            <li key={p.personId}>
              <button type="button" onClick={() => onPick(p)}>
                <Avatar name={p.name} size={30} />
                <span><strong>{p.name}</strong><small>{p.ref ?? ''}{p.positions.length ? ` · ${p.positions.map((x) => x.name).join(', ')}` : ''}</small></span>
                {!p.hasLogin && <span className="fos-chip attention" title="This person has no Compass login, so the assignment would give them no access">No login</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {!busy && dq.trim().length >= 2 && results.length === 0 && !error && <p className="fos-hint">No eligible people match. Only people who serve this scope can be assigned.</p>}
    </div>
  );
}

export default function AssignDrawer({ donorId, relationship, mode, onClose, onSaved }: { donorId: string; relationship: RelationshipView; mode: AssignMode; onClose: () => void; onSaved: () => void }) {
  const [role, setRole] = useState<AssignmentRole>(mode.kind === 'assign' ? mode.role : mode.kind === 'dates' || mode.kind === 'end' || mode.kind === 'reassign' ? mode.assignment.role : 'primary_coordinator');
  const [person, setPerson] = useState<AssignablePerson | null>(null);
  const [startedAt, setStartedAt] = useState(today());
  const [endsOn, setEndsOn] = useState(mode.kind === 'dates' ? mode.assignment.endsOn ?? '' : '');
  const [basis, setBasis] = useState(mode.kind === 'dates' ? mode.assignment.basisPositionId ?? '' : '');
  const [reason, setReason] = useState('');
  const [endedOn, setEndedOn] = useState(today());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const needsPerson = mode.kind === 'assign' || mode.kind === 'reassign';
  const titles = { assign: `Assign ${ROLE_LABEL[role].toLowerCase()}`, reassign: 'Reassign', end: 'End assignment', dates: 'Change dates' } as const;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    const common = { requestId: newRequestId() };
    try {
      if (mode.kind === 'assign') {
        await fosApi.assignment({ ...common, operation: 'assign', donorId, scope: scopeFromKey(relationship.scopeKey), role, assigneePersonId: person!.personId, startedAt, ...(endsOn ? { endsOn } : {}), ...(basis ? { basisPositionId: basis } : {}) });
      } else if (mode.kind === 'reassign') {
        await fosApi.assignment({ ...common, operation: 'reassign', assignmentId: mode.assignment.id, assigneePersonId: person!.personId, startedAt, ...(endsOn ? { endsOn } : {}), ...(basis ? { basisPositionId: basis } : {}), reason });
      } else if (mode.kind === 'end') {
        await fosApi.assignment({ ...common, operation: 'end', assignmentId: mode.assignment.id, endedOn, reason });
      } else {
        await fosApi.assignment({ ...common, operation: 'update', assignmentId: mode.assignment.id, expectedVersion: mode.assignment.version, endsOn: endsOn || null, basisPositionId: basis || null });
      }
      onSaved();
    } catch (cause) { setError(fosError(cause).message); } finally { setBusy(false); }
  };

  const current = mode.kind !== 'assign' ? mode.assignment : null;
  return (
    <Drawer title={titles[mode.kind]} subtitle={`${relationship.scopeLabel} relationship`} onClose={onClose}>
      <form onSubmit={submit} className="fos-form">
        {mode.kind === 'assign' && (
          <label>Role
            <select value={role} onChange={(e) => setRole(e.target.value as AssignmentRole)}>
              {(Object.keys(ROLE_LABEL) as AssignmentRole[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
            <small className="fos-hint">{ROLE_HINT[role]}</small>
          </label>
        )}
        {current && (
          <div className="fos-current"><span className="subtitle">{ROLE_LABEL[current.role]}</span>
            <div className="fos-person-line"><Avatar name={current.assignee.name} size={30} /><strong>{current.assignee.name}</strong></div>
            <span className="subtitle">Since {formatDate(current.startedAt)}{current.endsOn ? ` · ends ${formatDate(current.endsOn)}` : ''}</span></div>
        )}
        {needsPerson && (
          <div>
            <div className="fos-label">{mode.kind === 'reassign' ? 'New person' : 'Person'}</div>
            <PersonSearch donorId={donorId} scopeKey={relationship.scopeKey} value={person} onPick={setPerson} />
            {person && !person.hasLogin && <p className="fos-warn"><Icon name="alert" size={14} /> {person.name} has no Compass login, so this assignment would not give them access until they are given one.</p>}
          </div>
        )}
        {needsPerson && <label>Starts<input type="date" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} required /></label>}
        {(needsPerson || mode.kind === 'dates') && (
          <label>Planned end <small className="fos-hint">(optional)</small>
            <input type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} min={needsPerson ? startedAt : undefined} />
            <small className="fos-hint">After this date the assignment no longer applies, even if nobody ends it.</small>
          </label>
        )}
        {(needsPerson ? person && person.positions.length > 0 : mode.kind === 'dates') && (
          <label>Assigned because they serve as <small className="fos-hint">(optional)</small>
            <select value={basis} onChange={(e) => setBasis(e.target.value)}>
              <option value="">No specific Position</option>
              {(person?.positions ?? (current?.basisPositionId ? [{ positionId: current.basisPositionId, name: current.basisPositionName ?? current.basisPositionId }] : [])).map((p) => <option key={p.positionId} value={p.positionId}>{p.name}</option>)}
            </select>
            <small className="fos-hint">If that Position ends, this assignment is flagged for review.</small>
          </label>
        )}
        {mode.kind === 'end' && <label>Ends on<input type="date" value={endedOn} onChange={(e) => setEndedOn(e.target.value)} required /></label>}
        {(mode.kind === 'end' || mode.kind === 'reassign') && (
          <label>Reason<textarea value={reason} onChange={(e) => setReason(e.target.value)} required maxLength={500} placeholder="Why is this changing? (kept in the history)" /></label>
        )}
        <ErrorNote message={error} />
        <div className="drawer-actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button className={`btn ${mode.kind === 'end' ? 'danger' : 'primary'}`} disabled={busy || (needsPerson && !person) || ((mode.kind === 'end' || mode.kind === 'reassign') && !reason.trim())}>
            {busy ? <Spinner /> : mode.kind === 'end' ? 'End assignment' : mode.kind === 'reassign' ? 'Reassign' : mode.kind === 'dates' ? 'Save dates' : 'Assign'}
          </button>
        </div>
      </form>
    </Drawer>
  );
}
